import React from 'react';
import { DashboardData } from '../types';
import Button from './Button';
import ImageWithModal from './ImageWithModal';
import ArrowTrendingUpIcon from './icons/ArrowTrendingUpIcon';
import CreditCardIcon from './icons/CreditCardIcon';
import ChevronRightIcon from './icons/ChevronRightIcon';
import { useTranslation } from 'react-i18next';

interface DashboardViewProps {
  dashboardData: DashboardData | null;
  onGoToHistory: () => void;
  onGoToAccount: () => void;
}

interface StatCardProps {
  icon: React.ReactNode;
  label: string;
  value: string | number;
  action?: React.ReactNode;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, action, onClick }) => {
  const baseClasses = "bg-slate-50 border border-slate-200/80 p-4 rounded-lg flex flex-col items-center justify-center text-center shadow-sm";
  const clickableClasses = onClick ? "cursor-pointer transition-all duration-200 hover:bg-slate-100 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2" : "";

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick();
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    // If the action itself is a button with an onClick, it will fire.
    // We just need to stop propagation so the card's onClick doesn't also fire.
    e.stopPropagation();
  };

  return (
    <div
      className={`${baseClasses} ${clickableClasses}`}
      onClick={onClick}
      onKeyDown={handleKeyDown}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      <div className="flex items-center justify-center w-10 h-10 bg-sky-100 text-sky-600 rounded-full mb-2">
        {icon}
      </div>
      <p className="text-slate-600 text-xs font-medium">{label}</p>
      <p className="text-2xl md:text-3xl font-bold text-sky-700 my-1">{value}</p>
      {action && <div className="mt-1" onClick={handleActionClick}>{action}</div>}
    </div>
  );
};

const DashboardView: React.FC<DashboardViewProps> = ({ dashboardData, onGoToHistory, onGoToAccount }) => {
  const { t } = useTranslation();
  if (!dashboardData) {
    return <div>{t('dashboardView.loading')}</div>;
  }

  return (
    <div className="bg-white p-4 sm:p-6 md:p-8 shadow-xl rounded-lg">
      <div className="mb-6 text-center sm:text-left">
        <h2 className="text-2xl md:text-3xl font-bold text-sky-700">{t('dashboardView.title')}</h2>
      </div>
      <div className="grid grid-cols-2 gap-4 md:gap-6">
        <StatCard
          icon={<ArrowTrendingUpIcon className="w-5 h-5" />}
          label={t('dashboardView.totalSolutionsLabel')}
          value={dashboardData.totalSolutions}
          onClick={onGoToHistory}
        />
        <StatCard
          icon={<CreditCardIcon className="w-5 h-5" />}
          label={t('dashboardView.creditsRemainingLabel')}
          value={dashboardData.creditsRemaining}
          onClick={onGoToAccount}
        />
      </div>
      <div className="mt-8">
        <h3 className="text-lg md:text-xl font-semibold text-slate-800 mb-4">{t('dashboardView.solutionsTitle')}</h3>
        {dashboardData.recentSolutions.length > 0 ? (
          <ul className="space-y-3">
            {dashboardData.recentSolutions.map(item => (
              <li key={item.id} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between hover:bg-slate-100 transition-colors cursor-pointer" onClick={onGoToHistory} role="button" tabIndex={0}>
                <div className="flex items-center flex-grow min-w-0">
                  <ImageWithModal src={item.originalImageSrc} alt="Problem thumbnail" className="w-10 h-10 md:w-12 md:h-12 object-cover rounded-md mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{t('dashboardView.gradeLabel')}: {item.solution_jsonb?.math_solutions?.[0]?.problem_grade || item.grade}</p>
                    <p className="text-xs text-slate-500">{t('dashboardView.solvedLabel')}: {new Date(item.timestamp * 1000).toLocaleDateString('sr-RS', { day: '2-digit', month: '2-digit', year: '2-digit' })}</p>
                  </div>
                </div>
                <ChevronRightIcon className="w-5 h-5 text-slate-400 ml-2 flex-shrink-0" />
              </li>
            ))}
          </ul>
        ) : (
          <div className="text-center py-8 px-4 bg-slate-50 rounded-lg">
            <p className="text-slate-600">{t('dashboardView.noRecentSolutions')}</p>
            <p className="text-sm text-slate-500 mt-1">{t('dashboardView.solveFirstProblemHint')}</p>
          </div>
        )}
        <div className="text-center mt-6">
          <Button onClick={onGoToHistory} variant="secondary">{t('dashboardView.viewAllSolutionsButton')}</Button>
        </div>
      </div>
    </div>
  );
};

export default DashboardView;